import { FacetConfig, FacetConfigError } from "@globalgiving/vast-frontend-common"
import { InvalidFacetInfo } from "~/types"

/**
 * A facet's own label/category is only safe to show on a fallback if it independently passes
 * a plain type check here — the label itself can be one of the malformed properties.
 */
function safeDisplayName(facetConfig: FacetConfig | undefined): string | undefined {
  const label = facetConfig?.definition?.label
  if (typeof label === "string" && label.trim().length > 0) {
    return label
  }

  const category = facetConfig?.category
  if (typeof category === "string" && category.trim().length > 0) {
    return category
  }

  return undefined
}

/**
 * Logs a facet construction failure and returns only the non-sensitive info needed to render
 * a content-scoped fallback. Never includes raw config/value content beyond a label/category
 * that passed its own check above.
 */
export function reportInvalidFacet(facetConfig: FacetConfig | undefined, error: unknown): InvalidFacetInfo {
  const timestamp = new Date().toISOString()

  if (error instanceof FacetConfigError) {
    console.error("[Facet] Config validation failed", {
      facetId: facetConfig?.facet_id,
      facetName: facetConfig?.name,
      validation: error.validation,
      timestamp,
    })
  } else {
    console.error("[Facet] Unexpected error while constructing facet", {
      facetId: facetConfig?.facet_id,
      facetName: facetConfig?.name,
      error,
      timestamp,
    })
  }

  return {
    facetName: facetConfig?.name,
    displayName: safeDisplayName(facetConfig),
    timestamp,
  }
}
